import { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from "@mui/material";
import { AlertTriangle, Download } from "react-feather";
import { authData } from "../../components/GetAuth";
import { downloadFile, formatGBP2, saveBilling } from "../../components/Features";
import billingStyles from "../../components/billing.module.css";
import styles from "./addons.module.css";

const STATUS_LABEL = { trial: "Free trial", active: "Active", suspended: "Suspended", cancelled: "Ended" };
const CHIP = { trial: "ChipTrial", active: "ChipActive", suspended: "ChipSuspended", cancelled: "ChipVoid", pending: "ChipPending", overdue: "ChipOverdue", paid: "ChipPaid", void: "ChipVoid" };
const CANCEL_REASONS = ["Too expensive", "Missing features I need", "Not using it enough", "Switching to another provider", "Closing or pausing the business", "Other"];

export const StatusChip = ({ status, label }) => (<span className={`${billingStyles.Chip} ${billingStyles[CHIP[status]] || ""}`}>{label || status}</span>);

const fmt = (d) => (d ? dayjs(d).format("DD MMM YYYY") : "—");

// Company admin: plan status, unsubscribe and invoice history (payment is arranged offline)
const BillingInvoices = ({ showMessage }) => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const isAdmin = auth?.user_type === 2;
  const [invoices, setInvoices] = useState([]);
  const [payment, setPayment] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${API_TOKEN}` };
    Promise.allSettled([
      axios.get(process.env.REACT_APP_API_URL + "v1/billing/invoices", { headers }),
      axios.get(process.env.REACT_APP_API_URL + "v1/billing/summary", { headers }),
    ]).then(([invRes, sumRes]) => {
      if (invRes.status === "fulfilled" && invRes.value.data.status === "SUCCESS") {
        setInvoices(invRes.value.data.data);
        setPayment(invRes.value.data.payment);
      }
      if (sumRes.status === "fulfilled" && sumRes.value.data.status === "SUCCESS") {
        setSummary(sumRes.value.data.data);
        saveBilling(sumRes.value.data.data);
      }
      setLoaded(true);
    });
  }, [API_TOKEN]);

  const download = (inv) => {
    downloadFile(`v1/billing/invoices/${inv.id}/pdf`, `${inv.invoice_number}.pdf`).catch(() => showMessage("Could not download the invoice.", "error"));
  };

  const post = (path, data) => {
    setSaving(true);
    return axios.post(process.env.REACT_APP_API_URL + path, data || {}, { headers: { Authorization: `Bearer ${API_TOKEN}` } }).then((response) => {
      setSaving(false);
      if (response.data.status === "SUCCESS") {
        setSummary(response.data.data);
        saveBilling(response.data.data);
        showMessage(response.data.message);
        return true;
      }
      showMessage(response.data.message, "error");
      return false;
    }).catch(() => {
      setSaving(false);
      showMessage("Something went wrong. Please try again.", "error");
      return false;
    });
  };

  const unsubscribe = () => {
    const fullReason = [reason, reasonText.trim()].filter(Boolean).join(": ");
    post("v1/billing/cancel", { reason: fullReason }).then((ok) => {
      if (ok) {
        setCancelOpen(false);
        setReason("");
        setReasonText("");
      }
    });
  };

  const keepSubscription = () => post("v1/billing/cancel/undo");

  if (!loaded) return null;

  const outstanding = invoices.filter((i) => i.status === "pending" || i.status === "overdue").reduce((s, i) => s + Number(i.total || 0), 0);
  const inTrial = summary?.status === "trial";
  const cancelPending = !!summary?.cancels_at && summary?.status !== "cancelled";
  const ended = summary?.status === "cancelled";

  return (<div>
    <div className={`${styles.BillingTop}`}>
      <div className={`${styles.Stat}`}><span>Status</span><strong><StatusChip status={summary?.status} label={STATUS_LABEL[summary?.status]} /></strong></div>
      <div className={`${styles.Stat}`}><span>Monthly estimate</span><strong>{ended ? "—" : formatGBP2(summary?.monthly_estimate)}</strong></div>
      <div className={`${styles.Stat}`}>
        <span>{cancelPending || ended ? "Service ends" : inTrial ? "Trial ends" : "Next billing date"}</span>
        <strong>{cancelPending || ended ? fmt(summary?.cancels_at) : inTrial ? fmt(summary?.trial_ends_at) : fmt(summary?.next_billing_date)}</strong>
      </div>
      <div className={`${styles.Stat}`}><span>Outstanding</span><strong style={{ color: outstanding > 0 ? "#b42318" : undefined }}>{formatGBP2(outstanding)}</strong></div>
    </div>

    {/* Subscription: unsubscribe / keep / ended */}
    {!ended && <div className={`${styles.SubPanel} ${cancelPending ? styles.SubPanelWarn : ""}`}>
      {cancelPending ? (<>
        <div className={`${styles.SubPanelText}`}>
          <h3><AlertTriangle size={18} /> Your subscription ends on {fmt(summary.cancels_at)}</h3>
          <p>
            Everything keeps working until then, including your add-ons and the driver and customer apps.
            {inTrial ? " You are in your free trial, so you won't be charged." : " Your final invoice, for the period ending that day, is emailed as usual. Nothing is charged after that."}
          </p>
        </div>
        {isAdmin && <button type="button" className={`${billingStyles.PrimaryBU}`} disabled={saving} onClick={keepSubscription}>Keep my subscription</button>}
      </>) : (<>
        <div className={`${styles.SubPanelText}`}>
          <h3>Subscription</h3>
          <p>
            Basic plan {formatGBP2(summary?.basic_price)}/month{summary?.basic_discount?.label ? ` (${summary.basic_discount.label} the standard ${formatGBP2(summary?.basic_list_price)})` : ""} plus your add-ons.
            {inTrial ? ` Your free trial runs until ${fmt(summary?.trial_ends_at)} and your first invoice is emailed on ${fmt(summary?.next_invoice_date)}.` : ` Your next invoice is emailed on ${fmt(summary?.next_invoice_date)}.`}
            {" "}You can unsubscribe at any time; your service continues until the end of the current {inTrial ? "trial" : "billing period"}.
          </p>
        </div>
        {isAdmin && <button type="button" className={`${billingStyles.SecondaryBU}`} onClick={() => setCancelOpen(true)}>Unsubscribe</button>}
      </>)}
    </div>}

    <div className={`${styles.TableWrap}`}>
      {invoices.length === 0
        ? <div className={`${styles.Empty}`}>No invoices yet. {!ended && !cancelPending && summary?.next_invoice_date ? `Your first invoice will be emailed on ${fmt(summary.next_invoice_date)}.` : ""}</div>
        : <table className={`${styles.Table}`}>
          <thead>
            <tr><th>Invoice</th><th>Period</th><th>Issued</th><th>Due</th><th>Total</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (<tr key={inv.id}>
              <td><strong>{inv.invoice_number}</strong></td>
              <td>{dayjs(inv.period_start).format("DD MMM")} – {fmt(inv.period_end)}</td>
              <td>{fmt(inv.issue_date)}</td>
              <td>
                {fmt(inv.due_date)}
                {inv.status === "overdue" && !ended && <div style={{ fontSize: 12, color: "#b42318" }}>Service stops after {dayjs(inv.suspend_on).format("DD MMM")}</div>}
              </td>
              <td>{formatGBP2(inv.total)}</td>
              <td><StatusChip status={inv.status} /></td>
              <td align="right"><button type="button" className={`${styles.IconBU}`} onClick={() => download(inv)}><Download />PDF</button></td>
            </tr>))}
          </tbody>
        </table>}
    </div>

    <div className={`${styles.PayBox}`}>
      <h3>How to pay</h3>
      <p>{payment?.instructions || "Payment details are on each invoice. Please contact us if you need them again."}</p>
      <p style={{ marginTop: 10 }}>Use the invoice number as your payment reference. Invoices are due on the billing date; after a {summary?.grace_period_days ?? 7}-day grace period, unpaid accounts are paused until payment is received.{payment?.billing_email ? ` Questions: ${payment.billing_email}` : ""}</p>
    </div>

    <Dialog open={cancelOpen} onClose={() => !saving && setCancelOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Unsubscribe from Ezy Freight Logistics?</DialogTitle>
      <DialogContent>
        <ul className={`${styles.CancelList}`}>
          <li>Your service stays fully active until <strong>{fmt(summary?.cancel_date)}</strong>, the end of your current {inTrial ? "free trial" : "billing period"}.</li>
          <li>{inTrial ? "You won't be charged anything." : "Your final invoice, for the period ending that day, is emailed as usual. There are no charges after that."}</li>
          <li>After that date, your team, drivers and customers can no longer use the system or the apps. Your data is kept and you can resubscribe at any time.</li>
          <li>You can change your mind and keep your subscription any time before {fmt(summary?.cancel_date)}.</li>
        </ul>
        <TextField select label="Why are you leaving? (optional)" value={reason} onChange={(e) => setReason(e.target.value)} fullWidth size="small" margin="dense">
          <MenuItem value="">Prefer not to say</MenuItem>
          {CANCEL_REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
        </TextField>
        <TextField label="Anything we could do better? (optional)" value={reasonText} onChange={(e) => setReasonText(e.target.value)} fullWidth size="small" margin="dense" multiline minRows={2} inputProps={{ maxLength: 1000 }} />
      </DialogContent>
      <DialogActions sx={{ padding: "0 24px 20px" }}>
        <button type="button" className={`${billingStyles.PrimaryBU}`} disabled={saving} onClick={() => setCancelOpen(false)}>Keep my subscription</button>
        <button type="button" className={`${billingStyles.SecondaryBU}`} disabled={saving} onClick={unsubscribe}>{saving ? "Saving…" : `Unsubscribe, ends ${fmt(summary?.cancel_date)}`}</button>
      </DialogActions>
    </Dialog>
  </div>);
}

export default BillingInvoices;
